import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../firebaseConfig";
import { buildConferenceInfoMap } from "../../../../utils/conferenceNotes";
import { parseStudentDate } from "../../student/studentUtils";

const DIAGNOSTIC_COL = "diagnostic_results";

function parseWindowDays(value, fallback) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return fallback;
  return Math.round(parsed);
}

export const SCHEDULE_NOTIFICATION_WINDOWS = {
  conferenceDays: parseWindowDays(process.env.REACT_APP_SCHEDULE_CONFERENCE_NOTIFICATION_DAYS, 20),
  diagnosticDays: parseWindowDays(process.env.REACT_APP_SCHEDULE_DIAGNOSTIC_NOTIFICATION_DAYS, 20),
};

const DEFAULT_DIAGNOSTIC_ACTIVE_DAYS = SCHEDULE_NOTIFICATION_WINDOWS.diagnosticDays;

function parseDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value?.toDate === "function") {
    const d = value.toDate();
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value?.seconds === "number") {
    const d = new Date(value.seconds * 1000);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function normalizeStatus(status) {
  const value = String(status || "").trim().toLowerCase();
  if (value === "cancelled" || value === "canceled") return "cancelled";
  if (value === "confirmed") return "confirmed";
  if (value === "present") return "present";
  if (value === "pending") return "pending";
  return value || "pending";
}

function isCancelledSession(session) {
  return normalizeStatus(session?.status) === "cancelled";
}

function toDayStamp(value) {
  const d = parseDateValue(value);
  if (!d) return NaN;
  const copy = new Date(d);
  copy.setHours(0, 0, 0, 0);
  return copy.getTime();
}

function detectDiagnosticUid(docId, data) {
  const sid = String(data?.studentId || "").trim();
  if (sid) return sid;
  const match = String(docId || "").match(/^([^_]+)_(act|sat)_diagnostic_test/i);
  return match?.[1] || "";
}

function detectDiagnosticType(docId, data) {
  const explicit = String(data?.type || "").trim().toUpperCase();
  if (explicit === "ACT") return "ACT";
  if (explicit === "SAT") return "SAT";
  const id = String(docId || "").toLowerCase();
  if (id.includes("_act_")) return "ACT";
  return "SAT";
}

function detectDiagnosticCompletedAt(data) {
  return (
    parseDateValue(data?.completedAt) ||
    parseDateValue(data?.submittedAt) ||
    parseDateValue(data?.updatedAt) ||
    parseDateValue(data?.createdAt)
  );
}

function isCompletedDiagnostic(data) {
  if (!data || data?.isArchived) return false;
  return String(data?.status || "").toLowerCase() === "completed" || !!data?.completedAt || !!data?.submittedAt;
}

function formatDiagnosticDateLabel(date) {
  const parsed = parseDateValue(date);
  if (!parsed) return "";
  return parsed.toLocaleDateString();
}

function buildStudentMap(students = [], studentsById = {}) {
  if (studentsById && typeof studentsById === "object" && Object.keys(studentsById).length > 0) {
    return studentsById;
  }

  const map = {};
  for (let i = 0; i < (students || []).length; i += 1) {
    const student = students[i];
    if (!student?.id) continue;
    map[student.id] = student;
  }
  return map;
}

function buildFirstValidSessionStartByStudent(sessions = []) {
  const map = {};

  for (let i = 0; i < (sessions || []).length; i += 1) {
    const session = sessions[i] || {};
    const studentId = session.studentId;
    if (!studentId || session?.isDeleted || isCancelledSession(session)) continue;

    const start = parseDateValue(session.start);
    if (!start) continue;

    const ms = start.getTime();
    if (!Number.isFinite(map[studentId]) || ms < map[studentId]) {
      map[studentId] = ms;
    }
  }

  return map;
}

function hasPriorSessionHistory(student) {
  const lastSessionDate =
    parseStudentDate(student?.lastSessionDate) ||
    parseStudentDate(student?.endDate);
  if (lastSessionDate) return true;

  if (student?.hasAnySession === true) return true;

  const hours = Number(student?.totalSessionHours || 0);
  return Number.isFinite(hours) && hours > 0;
}

function getExplicitFirstSessionDate(student) {
  if (!student || typeof student !== "object") return null;
  return (
    parseStudentDate(student?.firstSessionDate) ||
    parseStudentDate(student?.firstSession) ||
    parseStudentDate(student?.firstScheduledSessionDate)
  );
}

function isFirstSessionNotificationTarget({
  session,
  student,
  firstVisibleSessionStartByStudent = {},
}) {
  if (!session || session?.isDeleted || isCancelledSession(session)) return false;
  const start = parseDateValue(session.start);
  if (!start) return false;

  // "First session" means first actual tutoring session, not "first attend"/signup date.
  // If we already have session history, never mark as first session.
  if (hasPriorSessionHistory(student)) return false;

  const firstSessionDate = getExplicitFirstSessionDate(student);
  if (firstSessionDate) {
    return toDayStamp(firstSessionDate) === toDayStamp(start);
  }

  const fallbackMs = firstVisibleSessionStartByStudent?.[session.studentId];
  return Number.isFinite(fallbackMs) && fallbackMs === start.getTime();
}

export async function fetchDiagnosticNotificationsByStudentId(
  students = [],
  { activeDays = DEFAULT_DIAGNOSTIC_ACTIVE_DAYS, now = new Date() } = {}
) {
  const studentRows = (students || []).filter((student) => student?.id);
  if (!studentRows.length) return {};

  const uidToStudentIds = new Map();
  for (let i = 0; i < studentRows.length; i += 1) {
    const student = studentRows[i];
    const diagnosticUid = String(student?.diagnosticUid || student?.id || "").trim();
    if (!diagnosticUid) continue;

    if (!uidToStudentIds.has(diagnosticUid)) {
      uidToStudentIds.set(diagnosticUid, []);
    }
    uidToStudentIds.get(diagnosticUid).push(student.id);
  }

  if (!uidToStudentIds.size) return {};

  const latestByUidType = new Map();
  const diagSnap = await getDocs(collection(db, DIAGNOSTIC_COL));
  const nowMs = now.getTime();
  const maxAgeMs = Number(activeDays) > 0 ? Number(activeDays) * 24 * 60 * 60 * 1000 : 0;

  diagSnap.forEach((diagDoc) => {
    const data = diagDoc.data() || {};
    if (!isCompletedDiagnostic(data)) return;

    const diagnosticUid = detectDiagnosticUid(diagDoc.id, data);
    if (!diagnosticUid || !uidToStudentIds.has(diagnosticUid)) return;

    const type = detectDiagnosticType(diagDoc.id, data);
    const completedAt = detectDiagnosticCompletedAt(data);
    const completedMs = completedAt ? completedAt.getTime() : 0;

    if (maxAgeMs > 0 && completedMs > 0 && nowMs - completedMs > maxAgeMs) return;

    const key = `${diagnosticUid}|${type}`;
    const current = latestByUidType.get(key);
    if (!current || completedMs > current.completedMs) {
      latestByUidType.set(key, {
        type,
        completedAt,
        completedMs,
      });
    }
  });

  const output = {};
  uidToStudentIds.forEach((studentIds, diagnosticUid) => {
    const satInfo = latestByUidType.get(`${diagnosticUid}|SAT`);
    const actInfo = latestByUidType.get(`${diagnosticUid}|ACT`);
    const notifications = [];

    if (satInfo) {
      notifications.push({
        id: "diagnostic_sat",
        type: "diagnostic",
        diagnosticType: "SAT",
        title: "SAT Diagnostic Report",
        subtitle: satInfo.completedAt
          ? `Completed ${formatDiagnosticDateLabel(satInfo.completedAt)}`
          : "New SAT diagnostic report is available",
        completedAt: satInfo.completedAt ? satInfo.completedAt.toISOString() : null,
      });
    }

    if (actInfo) {
      notifications.push({
        id: "diagnostic_act",
        type: "diagnostic",
        diagnosticType: "ACT",
        title: "ACT Diagnostic Report",
        subtitle: actInfo.completedAt
          ? `Completed ${formatDiagnosticDateLabel(actInfo.completedAt)}`
          : "New ACT diagnostic report is available",
        completedAt: actInfo.completedAt ? actInfo.completedAt.toISOString() : null,
      });
    }

    if (!notifications.length) return;
    studentIds.forEach((studentId) => {
      output[studentId] = notifications;
    });
  });

  return output;
}

export function buildSessionNotificationsBySessionId({
  sessions = [],
  students = [],
  studentsById = {},
  diagnosticNotificationsByStudentId = {},
  conferenceActiveDays = SCHEDULE_NOTIFICATION_WINDOWS.conferenceDays,
  now = new Date(),
} = {}) {
  const studentMap = buildStudentMap(students, studentsById);
  const studentRows = Object.values(studentMap);
  const conferenceInfoByStudentId = buildConferenceInfoMap(studentRows, {
    activeDays: conferenceActiveDays,
    now,
  });

  const firstVisibleSessionStartByStudent = buildFirstValidSessionStartByStudent(sessions);
  const result = {};

  for (let i = 0; i < (sessions || []).length; i += 1) {
    const session = sessions[i] || {};
    if (!session?.id) continue;

    const studentId = session.studentId;
    const student = studentMap?.[studentId] || null;
    const notifications = [];

    const conferenceInfo = conferenceInfoByStudentId?.[studentId] || null;
    if (conferenceInfo?.isActive) {
      notifications.push({
        id: "parent_conference",
        type: "conference",
        title: "Parent Conference Note",
        subtitle: conferenceInfo?.dateLabel ? `Date: ${conferenceInfo.dateLabel}` : "Recent parent conference note",
        dateLabel: conferenceInfo?.dateLabel || "",
        noteHtml: conferenceInfo?.noteHtml || "",
      });
    }

    const diagnosticItems = diagnosticNotificationsByStudentId?.[studentId];
    if (Array.isArray(diagnosticItems) && diagnosticItems.length) {
      notifications.push(...diagnosticItems);
    }

    if (
      isFirstSessionNotificationTarget({
        session,
        student,
        firstVisibleSessionStartByStudent,
      })
    ) {
      notifications.push({
        id: "first_session",
        type: "first_session",
        title: "First Session",
        subtitle: "This student is attending a first session.",
      });
    }

    result[session.id] = notifications;
  }

  return result;
}
